import { inject, Injectable } from '@angular/core';
import { ApiService } from '../api/api.service';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { User } from '@angular/fire/auth';
import { BrowserStorage } from '../../utils/browserStorage';
import { isNullOrUndefined, isValidString } from '../../utils/functions.utils';
import { IToken } from '../../interfaces/token.interface';
import { ProjectService } from '../project/project.service';
import { getCurrentLanguage } from '../../utils/language.utils';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  readonly _api = inject(ApiService);
  readonly _auth = inject(AngularFireAuth);
  readonly _router = inject(Router);
  readonly _projectService = inject(ProjectService);
  private readonly dialog = inject(MatDialog);

  isLogged = new BehaviorSubject<boolean>(false);
  firebaseUser: User | null = null;

  currentRole = new BehaviorSubject<string | null>(null);

  refreshRoleForProject(projectId: string | null): void {
    if (!projectId) {
      this.currentRole.next(null);
      return;
    }

    const token = BrowserStorage.get('userToken');
    const projects = this.getProjectsClaimFromToken(token);
    const projectClaim = projects.find((claim) => claim.projectId === projectId);
    this.currentRole.next(projectClaim?.roleName ?? null);
  }

  getCurrentRole(): string | null {
    return this.currentRole.value;
  }

  private getProjectsClaimFromToken(token: string | null): { projectId: string; roleName: string | null }[] {
    if (!token) {
      return [];
    }

    try {
      const payloadSegment = token.split('.')[1];
      if (!payloadSegment) {
        return [];
      }
      const normalized = payloadSegment.replace(/-/g, '+').replace(/_/g, '/');
      const decoded = JSON.parse(atob(normalized));
      return Array.isArray(decoded?.projects) ? decoded.projects : [];
    } catch {
      return [];
    }
  }

  async signupWithEmail(email: string, password: string, fullName: string) {
    const token = await this._api.post<IToken>('/auth/signup', {
      body: { email, password, fullName, language: getCurrentLanguage() },
      showAPIError: true,
      showAPISuccess: false,
    });
    await this.signInWithCustomToken(token?.data?.accessToken);
  }

  async signinWithEmail(email: string, password: string, returnUrl?: string | null) {
    const token = await this._api.post<IToken>('/auth/signin', {
      body: { email, password },
      showAPIError: true,
      showAPISuccess: false,
    });
    await this.signInWithCustomToken(token?.data?.accessToken, returnUrl);
  }

  async signupWithInvitationToken(email: string, password: string, fullName: string, invitationToken: string) {
    const token = await this._api.post<IToken>('/auth/invite/signup', {
      body: { email, password, fullName, invitationToken, language: getCurrentLanguage() },
      showAPIError: true,
      showAPISuccess: false,
    });
    await this.signInWithCustomToken(token?.data?.accessToken);
  }

  async requestPasswordReset(email: string): Promise<void> {
    await this._api.post<void>('/auth/forgot-password', {
      body: { email },
      showAPIError: true,
      showAPISuccess: false,
    });
  }

  async resetPassword(token: string, password: string): Promise<void> {
    await this._api.post<void>('/auth/reset-password', {
      body: { token, password },
      showAPIError: false,
      showAPISuccess: false,
    });
  }

  async acceptInvitation(invitationToken: string): Promise<{ projectId: string; projectName: string }> {
    const response = await this._api.post<{ projectId: string; projectName: string }>('/auth/invite/accept', {
      body: { invitationToken },
      showAPIError: true,
      showAPISuccess: false,
    });
    return response.data;
  }

  async signInWithCustomToken(customToken: string, returnUrl?: string | null): Promise<void> {
    const credential = await this._auth.signInWithCustomToken(customToken);
    const token = await credential.user?.getIdToken();
    if (token) {
      BrowserStorage.set('userToken', token);
      this.isLogged.next(true);
      await this.navigateAfterAuthentication(returnUrl);
    }
  }

  async redirectAuthenticatedUser(returnUrl?: string | null): Promise<void> {
    await this.navigateAfterAuthentication(returnUrl);
  }

  // Firebase silently refreshes the ID token in the background roughly every hour (its fixed,
  // non-configurable lifetime); without this, the stored token goes stale and every request
  // eventually 401s, forcing a logout mid-session. AngularFireAuth's `idToken` observable emits
  // on every refresh, keeping the stored token current for as long as the app stays open.
  //
  // On a page load the stored token is whatever the previous session left behind, and it may
  // already be expired. Awaiting Firebase's own token here — it refreshes it when needed — before
  // the app boots is what keeps the first requests from 401ing and signing the user out. Awaiting
  // only the restored user (setIsLogged) is not enough: it resolves before the refreshed token
  // arrives, so those requests would still go out with the expired one.
  async listenTokenChanges(): Promise<void> {
    this._auth.idToken.subscribe((token) => {
      if (token) {
        BrowserStorage.set('userToken', token);
      }
    });

    try {
      await this.getFirebaseIdToken();
    } catch (error) {
      console.error('Error refreshing the stored ID token on startup:', error);
      await this.setIsLogged();
    }
  }

  async getFirebaseIdToken(): Promise<string | null> {
    const firebaseUser = await firstValueFrom(this._auth.user);

    if (!firebaseUser) {
      BrowserStorage.delete('userToken');
      this.isLogged.next(false);
      return null;
    }

    const token = await firebaseUser.getIdToken();
    BrowserStorage.set('userToken', token);
    this.isLogged.next(true);
    return token;
  }

  async setIsLogged(): Promise<void> {
    if (!isValidString(BrowserStorage.get('userToken'))) {
      this.isLogged.next(false);
      return;
    }

    const firebaseUser = await firstValueFrom(this._auth.user);
    if (isNullOrUndefined(firebaseUser)) {
      this.isLogged.next(false);
      return;
    }
    this.isLogged.next(true);
  }

  async isAuth(): Promise<boolean> {
    if (!isValidString(BrowserStorage.get('userToken'))) {
      this.isLogged.next(false);
      return false;
    }

    const firebaseUser = await firstValueFrom(this._auth.user);
    if (isNullOrUndefined(firebaseUser)) {
      this.isLogged.next(false);
      return false;
    }
    this.isLogged.next(true);
    return true;
  }

  async signOut(): Promise<void> {
    await this.clearSession().finally(() => {
      this._router.navigate(['/signin']);
    });
  }

  async clearSession(): Promise<void> {
    await this._auth.signOut().finally(() => {
      this.dialog.closeAll();
      BrowserStorage.delete('userToken');
      this.isLogged.next(false);
      this.currentRole.next(null);
    });
  }

  private async navigateAfterAuthentication(returnUrl?: string | null): Promise<void> {
    const safeReturnUrl = this.getSafeReturnUrl(returnUrl);
    if (safeReturnUrl) {
      await this._router.navigateByUrl(safeReturnUrl);
      return;
    }

    await this.navigateToInitialProject();
  }

  private async navigateToInitialProject(): Promise<void> {
    const initialProjectId = await this._projectService.getInitialProjectId();
    if (initialProjectId) {
      await this._router.navigateByUrl(`/projects/${initialProjectId}/dashboard`);
    } else {
      await this._router.navigateByUrl('/projects/new');
    }
  }

  private getSafeReturnUrl(returnUrl?: string | null): string | null {
    if (!isValidString(returnUrl) || !returnUrl.startsWith('/')) {
      return null;
    }

    return returnUrl;
  }
}
